import {useCallback, useRef, useState} from "react";
import {useHistory, useParams} from 'react-router-dom';

import styled from "styled-components";
import PostList from '../../components/post_list';
import CommentList from "../../components/comment_list";
import ContentList from "../../components/content_list";
import WhiteMain from '../../components/main';
import TopBar from '../../components/top_bar';
import TopHeadroom from "../../components/top_headroom";
import ErrorMessage from "../../components/error_message";
import {useProfilePosts} from '../../api/feed';
import {useBottomBar} from "../../app_context";
import {useProfileComments} from '../../api/comment';
import useFeedback from '../../utils/use_feedback';
import {useFeedState, useStoreProfile, useUsername} from '../../store';
import {useQuery} from '../../utils/router';
import {useProfileOverview} from "../../api/content";
import {useProfile} from "../../api/user";
import ProfileToggles from "../../components/profile_toggles";

const NotFound = styled.div`
text-align: center;
padding-top: 32px;`;

export const NoContent = styled(({className}) => {
    return (
        <div className={className}>
            <p>Nothing here yet.</p>
        </div>
    );
})`
  width: 180px;
  margin: 0 auto;
  margin-top: 80px;
  text-align: center;
  p {
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 24px;
  }
`;

const ProfileOverview = ({user, feedbackCh, self}) => {
    const signalRef = useRef(null);
    const {content, loading, loaded, refresh, loadNextPage, polled} = useProfileOverview(feedbackCh, user, signalRef, signalRef);
    const history = useHistory();
    const openPost = useCallback((post) => {
        history.push(`/p/${post.uuid}`);
        // history.push(`${location.pathname}?post=${post.uuid}`);
    }, [history]);
    if (loaded && content.length === 0) {
        return <NoContent self={self} />
    }
    return (
        <ContentList
            feedKey={`u/${user}`}
            expand={'card'}
            loading={loading}
            content={content}
            refresh={refresh}
            loadNextPage={loadNextPage}
            onOpenPost={openPost}
            profile
            signalRef={signalRef}
            polled={polled}
            noBorders
        />
    );
};

const ProfilePosts = ({user, feedbackCh, self}) => {
    const {expand} = useStoreProfile();
    const signalRef = useRef(null);
    const key = `u/${user}/posts`;
    const feedState = useFeedState(key);
    const [restoredState, setRestoredState] = useState(feedState?.updatedTime > Date.now() - (15 * 60 * 1000) ? feedState : false);
    const {posts, loading, loaded, more, loadNextPage, polled} = useProfilePosts(feedbackCh, user, signalRef, key, !restoredState);
    if (loaded && posts.length === 0) {
        return <NoContent self={self} />
    }
    return (
        <PostList
            expand={expand}
            loading={loading}
            posts={posts}
            loadNextPage={more && loadNextPage}
            signalRef={signalRef}
            polled={polled}
            feedKey={key}
            restoredState={restoredState}
        />
    );
};

const ProfileComments = ({user, feedbackCh, self}) => {
    const {comments, loading, loaded, more, loadNextPage} = useProfileComments(feedbackCh, user);
    if (loaded && comments.length === 0) {
        return <NoContent self={self} />
    }
    return (
        <CommentList
            feedKey={`u/${user}`}
            comments={comments}
            loading={loading}
            loadNextPage={more && loadNextPage}
            profile
        />
    );
};

const ProfilePage = () => {
    const {error, feedbackCh} = useFeedback();
    const {user} = useParams();
    const username = useUsername();
    const isSelf = user.toLowerCase() === 'me' || username && user.toLowerCase() === username.toLowerCase();
    const {type} = useQuery();
    const profile = useProfile(feedbackCh, user);
    const viewable = !(profile?.is_deleted || profile?.is_suspended);
    useBottomBar();
    return (
        <>
                <TopBar title={user === 'me' ? 'Your Profile' : user} back profile={user} settings messageTo={user} border/>
                <WhiteMain>
                <ProfileToggles activeTopic={type} />
                <ErrorMessage error={error}/>
                {viewable && profile ?
                    <>
                        {(!type || type === 'overview') && <ProfileOverview user={user} feedbackCh={feedbackCh} self={isSelf}/>}
                        {type === 'post' && <ProfilePosts user={user} feedbackCh={feedbackCh} self={isSelf}/>}
                        {type === 'comment' && <ProfileComments user={user} feedbackCh={feedbackCh} self={isSelf}/>}
                    </>
                    :
                    <>
                        {profile &&
                            <NotFound>This user has {profile?.is_deleted ? 'deleted their account' : 'been suspended'}.</NotFound>
                        }
                    </>
                }
            </WhiteMain>
        </>
    );

};

export default ProfilePage;
